import { ActionCreatorWithPayload, Dispatch } from '@reduxjs/toolkit';
import { TFunction } from 'i18next';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch } from 'react-redux';
import { CRM_DROPDOWN_MAX_OPTIONS } from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import { RadioGroup } from '../../../components/RadioGroup';
import { ToggleButton } from '../../../components/ToggleButton';
import {
    DropdownOption,
    DropdownOptionsWithUndefined,
} from '../../../lib/Dropdown/types';
import { StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import { colourOptions } from './colours';

export type DispatchToStore = Dispatch<{
  payload: DropdownOption[] | string | null;
  type: string;
}>;

export type SetValueAction = ActionCreatorWithPayload<string | null, string>;

export const radioOptions = (
  t: TFunction,
  step: string,
  confirmOptions: string,
): {
  label: string;
  value: string;
}[] => [
  {
    label: t(`topics:${step}:options:${confirmOptions}:yes`),
    value: t(`topics:${step}:options:${confirmOptions}:yes`),
  },
  {
    label: t(`topics:${step}:options:${confirmOptions}:no`),
    value: t(`topics:${step}:options:${confirmOptions}:no`),
  },
];

// Calculate the status of the toggle buttons and conditional layout
// based on the value of one of the mandatory fields
export const handleConditionalLayout = (
  stateValue: string | null,
): {
  toggleStateValues: string;
  statefulNo: boolean;
  statefulYes: boolean;
} => {
  const statefulNo = stateValue === '-';
  const statefulYes = stateValue !== null && !statefulNo;
  const toggleStateValues = statefulYes ? 'Yes' : 'No';

  return { toggleStateValues, statefulNo, statefulYes };
};

export const handleFallbackFormulationDispatches = (
  toggled: boolean,
  value: string,
  setShowDetails: (value: React.SetStateAction<boolean>) => void,
  setToggleValue: (value: React.SetStateAction<string | null>) => void,
  dispatch: Dispatch<{
    payload: DropdownOption[] | string | null;
    type: string;
  }>,
  setStateValues: ActionCreatorWithPayload<string | null, string>[],
): void => {
  if (toggled) {
    // If one of the buttons is toggled active
    // and if the value of the button is 'No'
    // then hide the content and dispatch '-' values
    if (value === 'No') {
      setShowDetails(false);
      setToggleValue('No');
      batch(() => {
        setStateValues.forEach((setFunction) => {
          dispatch(setFunction('-'));
        });
      });
    } else {
      // If one of the buttons is toggled active
      // if the value of the button is 'Yes'
      // then show the remaining content
      // and dispatch null values to reset any saved old values
      setShowDetails(true);
      setToggleValue('Yes');
      batch(() => {
        setStateValues.forEach((setFunction) => dispatch(setFunction(null)));
      });
    }
  } else {
    // If none of the buttons are toggled active
    // then hide remaining content
    // and dispatch null values to reset any saved old values
    setShowDetails(false);
    setToggleValue(null);
    batch(() => {
      setStateValues.forEach((setFunction) => dispatch(setFunction(null)));
    });
  }
};

export const StepColour: FC<StepProps> = () => {
  const { t } = useTranslation();
  const [
    { colourShade, colourSamples, colourRequiredTime, colourLabSupport },
    {
      setColourShade,
      setColourSamples,
      setColourRequiredTime,
      setColourLabSupport,
    },
  ] = useBriefingState();
  const dispatch = useDispatch();

  const colourConfirmOptions = [
    t('topics:colour:options:confirmColourOptions:yes'),
    t('topics:colour:options:confirmColourOptions:no'),
  ];

  const translationColourOptions = colourOptions.map((colour) => ({
    name: t(`${colour.name}`),
    value: colour.value,
    hexCode: colour.hexCode,
  }));

  const { toggleStateValues, statefulYes } =
    handleConditionalLayout(colourSamples);
  const [showColourDetails, setShowColourDetails] = useState(statefulYes);
  const [colourSamplesNeeded, setColourSamplesNeeded] = useState(false);
  const [toggleValue, setToggleValue] = useState<string | null>(
    colourSamples === null ? null : toggleStateValues,
  );

  useEffect(() => {
    setColourSamplesNeeded(colourSamples === 'Yes');
  }, [colourSamples]);

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:colour:heading:general`)}
      />

      <TemplateFlexGridContainer.BriefingStepBody>
        <TemplateFlexGridContainer.BriefingStepSection>
          <Heading
            level="h3"
            margin={0}
            text={t('topics:colour:formulationColoured')}
          />
          <div className="w-full">
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {colourConfirmOptions.map((option) => {
                return (
                  <div key={option.toLowerCase()}>
                    <ToggleButton
                      dataTest={`${option}-toggle`}
                      text={option}
                      value={option.toLowerCase()}
                      on={toggleValue === option}
                      onToggle={(on: boolean) => {
                        handleFallbackFormulationDispatches(
                          on,
                          option,
                          setShowColourDetails,
                          setToggleValue,
                          dispatch,
                          [
                            setColourShade,
                            setColourSamples,
                            setColourRequiredTime,
                            setColourLabSupport,
                          ] as SetValueAction[],
                        );
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>

        {showColourDetails && typeof colourShade !== 'string' && (
          <div className="space-y-3">
            <Heading
              level="h3"
              margin={0}
              text={t('topics:colour:colourAttributes')}
            />
            <div className="space-y-6" data-testid="conditional-colour-content">
              <div className="w-full md:w-6/12">
                <InputGroup label={t('topics:colour:colourShades')}>
                  <Dropdown
                    initialSelection={colourShade || undefined}
                    options={translationColourOptions}
                    error={!translationColourOptions}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(option: DropdownOptionsWithUndefined) => {
                      dispatch(setColourShade(option as DropdownOption[]));
                    }}
                    icon={true}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>

              <div className="w-full md:w-6/12">
                <RadioGroup
                  label={t('topics:colour:colourSamples:heading')}
                  onChange={(selectedValue: string | null) => {
                    dispatch(setColourSamples(selectedValue));
                  }}
                  initialSelection={colourSamples}
                  radioOptions={radioOptions(
                    t,
                    'colour',
                    'confirmColourOptions',
                  )}
                />
              </div>

              {colourSamplesNeeded && (
                <div className="w-full md:w-8/12">
                  <InputGroup
                    label={t('topics:colour:requiredTime:heading')}
                    optional={true}
                  >
                    <div className="w-full">
                      <Input
                        initialValue={colourRequiredTime}
                        type="text"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          dispatch(setColourRequiredTime(e.target.value));
                        }}
                      />
                    </div>
                  </InputGroup>
                </div>
              )}

              <div className="w-full md:w-8/12">
                <RadioGroup
                  label={t('topics:colour:labSupport:heading')}
                  onChange={(selectedValue: string | null) => {
                    dispatch(setColourLabSupport(selectedValue));
                  }}
                  initialSelection={colourLabSupport}
                  radioOptions={radioOptions(
                    t,
                    'colour',
                    'confirmColourOptions',
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
